import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Fingerprint,
  AccessTime,
  Close,
  Visibility,
  Login as LoginIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { RootState } from '../../store/store';
import { attendanceApi } from '../../api/attendanceApi';
import { employeeApi } from '../../api/employeeApi';
import { geoLocationApi } from '../../api/geoLocationApi';
import { useNavigate } from 'react-router-dom';
const EMPLOYEE_LINK_HELP_MESSAGE =
  'Your login is active, but it is not yet linked to an employee profile for attendance. This usually happens when the employee record was created but login details were not linked to the same account. Please ask HR or your administrator to open your employee profile and verify the Login Details, Official Email, and account mapping. Once the login is linked to your employee profile, attendance will start working here.';

const WebPunchCard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [punchesOpen, setPunchesOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [punchAction, setPunchAction] = useState<'checkin' | 'checkout'>('checkin');
  const [todayPunches, setTodayPunches] = useState<any[]>([]);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [noEmployee, setNoEmployee] = useState(false);
  const [employeeShiftId, setEmployeeShiftId] = useState<string | null>(null);
  const [hasBiometricCheckIn, setHasBiometricCheckIn] = useState(false);
  const [biometricNotified, setBiometricNotified] = useState(false);
  const [allowMultipleCheckin, setAllowMultipleCheckin] = useState(false);
  const [webSelfieCheckinRequired, setWebSelfieCheckinRequired] = useState(false);
  const [webSelfieCheckoutRequired, setWebSelfieCheckoutRequired] = useState(false);
  const [selfieDataUrl, setSelfieDataUrl] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  // Live clock
  useEffect(() => {
    timerRef.current = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!employeeId) return;
    try {
      setStatusLoading(true);
      const status = await attendanceApi.getTodayStatus(employeeId);
      setIsCheckedIn(status.isCheckedIn);
      setTodayPunches(status.todayPunches);
      setAllowMultipleCheckin(Boolean(status.allowMultipleCheckin));
      setWebSelfieCheckinRequired(Boolean(status.isWebSelfieCheckinRequired));
      setWebSelfieCheckoutRequired(Boolean(status.isWebSelfieCheckoutRequired));
    } catch {
      setIsCheckedIn(false);
      setTodayPunches([]);
    } finally {
      setStatusLoading(false);
    }
  }, [employeeId]);

  // Resolve employee ID from current user
  useEffect(() => {
    const resolveEmployee = async () => {
      try {
        const emp = await employeeApi.getCurrentEmployee();
        setEmployeeId(emp.id);
        setNoEmployee(false);

        try {
          // Try to load the full employee record for shift validation.
          // Some self-service employee roles can resolve /employees/me but are not allowed to read /employees/{id}.
          const profile = await employeeApi.getById(emp.id);
          setEmployeeShiftId(profile.shiftId || null);
        } catch (error: any) {
          if (error?.response?.status === 403) {
            setEmployeeShiftId(null);
          } else {
            throw error;
          }
        }
      } catch {
        setNoEmployee(true);
        setStatusLoading(false);
      }
    };
    resolveEmployee();
  }, []);

  useEffect(() => {
    if (employeeId) fetchStatus();
  }, [employeeId, fetchStatus]);

  useEffect(() => {
    const biometricCheckIn = todayPunches.some(
      (punch) =>
        String(punch?.type || '').toUpperCase() === 'CHECK IN'
        && String(punch?.modeOfEntry || '').toLowerCase().includes('biometric')
    );
    setHasBiometricCheckIn(biometricCheckIn);
    if (!biometricCheckIn && biometricNotified) {
      setBiometricNotified(false);
    }
    // No toast here; show persistent sign below dashboard info instead
  }, [todayPunches, biometricNotified]);

  const handlePunchClick = async () => {
    // Always fetch latest employee profile before checking shift
    if (!employeeId) {
      toast.error(EMPLOYEE_LINK_HELP_MESSAGE);
      return;
    }
    try {
      const profile = await employeeApi.getById(employeeId);
      setEmployeeShiftId(profile.shiftId || null);
      if (!isCheckedIn && hasBiometricCheckIn) {
        toast.info('You are already signed in by biometric');
        return;
      }
      if (!profile.shiftId) {
        toast.error('This employee has not been assigned a shift');
        return;
      }
      setPunchAction(isCheckedIn ? 'checkout' : 'checkin');
      setRemarks('');
      setRemarksOpen(true);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        if (!isCheckedIn && hasBiometricCheckIn) {
          toast.info('You are already signed in by biometric');
          return;
        }

        setPunchAction(isCheckedIn ? 'checkout' : 'checkin');
        setRemarks('');
        setRemarksOpen(true);
        return;
      }

      toast.error('Failed to fetch employee profile');
    }
  };

  const captureAndSaveLocation = (empId: string, source: string) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          geoLocationApi.saveLocation({
            employeeId: empId,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            source,
          }).catch(() => {});
        },
        () => {
          geoLocationApi.saveLocation({
            employeeId: empId,
            latitude: 0,
            longitude: 0,
            locationName: 'Location unavailable',
            source,
          }).catch(() => {});
        }
      );
    }
  };

  const handleSubmitPunch = async () => {
    if (!employeeId) {
      toast.error(EMPLOYEE_LINK_HELP_MESSAGE);
      return;
    }

    try {
      setLoading(true);
      if (punchAction === 'checkin') {
        await attendanceApi.checkIn(employeeId, remarks || undefined, undefined, undefined, selfieDataUrl || undefined);
        toast.success('Checked in successfully!');
        setIsCheckedIn(true);
        captureAndSaveLocation(employeeId, 'Web Check-In');
      } else {
        await attendanceApi.checkOut(employeeId, remarks || undefined, undefined, undefined, selfieDataUrl || undefined);
        toast.success('Checked out successfully!');
        setIsCheckedIn(false);
        captureAndSaveLocation(employeeId, 'Web Check-Out');
      }
      setRemarksOpen(false);
      setRemarks('');
      // stop camera if open
      stopCamera();
      setSelfieDataUrl(null);
      // Refresh punches
      await fetchStatus();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Failed to ${punchAction === 'checkin' ? 'check in' : 'check out'}`);
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
    } catch (e) {
      console.error('Failed to start camera', e);
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        try { videoRef.current.pause(); } catch {}
        videoRef.current.srcObject = null;
      }
    } catch (e) {
      // ignore
    }
    setCameraActive(false);
  };

  const captureSelfie = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current || document.createElement('canvas');
    // Resize to limit payload size
    const maxWidth = 800;
    const aspect = (video.videoHeight || 480) / (video.videoWidth || 640);
    const width = Math.min(video.videoWidth || 640, maxWidth);
    const height = Math.round(width * aspect);
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
    setSelfieDataUrl(dataUrl);
    stopCamera();
  };

  const handleViewPunches = async () => {
    await fetchStatus();
    setPunchesOpen(true);
  };

  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });

  const accentColor = isCheckedIn ? '#2e7d32' : '#1565c0';
  const accentGradient = isCheckedIn
    ? 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)'
    : 'linear-gradient(135deg, #42a5f5 0%, #1565c0 100%)';

  // Determine if user is only admin
  const isAdminOnly = !!(user && (user.roleName === 'Admin' || (Array.isArray(user.roles) && user.roles.length === 1 && user.roles[0] === 'Admin')));

  if (isAdminOnly) return null;

  return (
    <>
      {/* Dashboard Info (assumed above) */}
     
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          transition: 'transform 0.2s, box-shadow 0.2s',
          height: '100%',
          minHeight: 320,
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          },
        }}
      >
        {/* Top accent bar */}
        <Box sx={{ height: 4, background: accentGradient }} />

        <CardContent
          sx={{
            p: 2.5,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
           {hasBiometricCheckIn && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<Fingerprint sx={{ fontSize: 18, color: '#2196f3' }} />}
            label="You are already signed in by biometric"
            color="info"
            sx={{ fontWeight: 600, fontSize: 15, px: 2, py: 1, bgcolor: '#e3f2fd', color: '#1565c0' }}
          />
        </Box>
      )}

          {/* Header row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  background: accentGradient,
                  borderRadius: 2,
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 14px ${accentColor}40`,
                }}
              >
                <Fingerprint sx={{ fontSize: 22, color: '#fff' }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} color={accentColor}>
                Web Checkin
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleViewPunches}
              title="View Punches"
              sx={{ color: 'text.secondary', '&:hover': { color: accentColor } }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Box>

          {/* Date */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {formatDate(currentTime)}
          </Typography>

          <Box sx={{ py: 0.5 }}>
            <Typography
              variant="h4"
              fontWeight={600}
              sx={{
                fontFamily: '"Roboto Mono", monospace',
                letterSpacing: 1.5,
                color: 'text.primary',
                lineHeight: 1.15,
                fontSize: { xs: '2rem', sm: '2.4rem' },
              }}
            >
              {formatTime(currentTime)}
            </Typography>
          </Box>

          {/* Status + Action */}
          {statusLoading ? (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <CircularProgress size={22} />
            </Box>
          ) : noEmployee ? (
            <Box sx={{ textAlign: 'center', py: 0.5 }}>
              <Chip
                icon={<AccessTime sx={{ fontSize: 14 }} />}
                label="Not Checked In"
                size="small"
                variant="outlined"
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 340, mx: 'auto' }}>
                Your login is not linked to an employee attendance profile yet.
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 340, mx: 'auto', mt: 0.5 }}>
                Please ask HR or your administrator to verify your employee login details and link this account to your employee profile.
              </Typography>
            </Box>
          ) : (
            <>
              <Button
                variant="contained"
                fullWidth
                size="medium"
                disableElevation
                startIcon={isCheckedIn ? <LogoutIcon /> : <LoginIcon />}
                onClick={handlePunchClick}
                disabled={!isCheckedIn && hasBiometricCheckIn}
                sx={{
                  py: 1.2,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  background: isCheckedIn
                    ? 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)'
                    : accentGradient,
                  '&:hover': {
                    background: isCheckedIn
                      ? 'linear-gradient(135deg, #e53935 0%, #c62828 100%)'
                      : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  },
                }}
              >
                {isCheckedIn ? 'Check Out' : 'Check In'}
              </Button>

              {/* Punches count / status line */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1, gap: 0.5 }}>
                <Chip
                  label={isCheckedIn ? 'Checked In' : 'Not Checked In'}
                  size="small"
                  sx={{
                    height: 28,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    bgcolor: isCheckedIn ? '#e8f5e9' : '#fafafa',
                    color: isCheckedIn ? '#2e7d32' : 'text.secondary',
                    border: '1px solid',
                    borderColor: isCheckedIn ? '#c8e6c9' : '#e0e0e0',
                  }}
                />
                {todayPunches.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { color: accentColor } }}
                    onClick={handleViewPunches}
                  >
                    &middot; {todayPunches.length} punch{todayPunches.length !== 1 ? 'es' : ''}
                  </Typography>
                )}
              </Box>
              <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/leave/leave-calendar')}
              sx={{
                mt: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              View Attendance Calendar 
            </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Remarks Dialog */}
      <Dialog open={remarksOpen} onClose={() => { if (!loading) { setRemarksOpen(false); stopCamera(); setSelfieDataUrl(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {punchAction === 'checkin' ? 'Check In' : 'Check Out'} Remarks
          </Typography>
          <IconButton onClick={() => !loading && setRemarksOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add any remarks for your {punchAction === 'checkin' ? 'check-in' : 'check-out'} (optional).
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Enter remarks..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            variant="outlined"
            autoFocus
          />
          {/* Selfie capture when required */}
          {((punchAction === 'checkin' && webSelfieCheckinRequired) || (punchAction === 'checkout' && webSelfieCheckoutRequired)) && (
            <Box sx={{ mt: 2 }}>
              {selfieDataUrl ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <img src={selfieDataUrl} alt="Selfie preview" style={{ maxWidth: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
                  <Box>
                    <Button size="small" variant="outlined" onClick={() => { setSelfieDataUrl(null); setTimeout(() => startCamera(), 50); }} sx={{ mr: 1 }}>Retake</Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <video ref={videoRef} style={{ width: '100%', borderRadius: 8, background: '#000' }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!cameraActive && (
                      <Button size="small" variant="contained" onClick={startCamera}>Open Camera</Button>
                    )}
                    {cameraActive && (
                      <Button size="small" variant="contained" onClick={captureSelfie}>Capture</Button>
                    )}
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemarksOpen(false)} disabled={loading} color="inherit">
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitPunch}
            disabled={loading || (((punchAction === 'checkin' && webSelfieCheckinRequired) || (punchAction === 'checkout' && webSelfieCheckoutRequired)) && !selfieDataUrl)}
            color={punchAction === 'checkin' ? 'success' : 'error'}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Punches Dialog */}
      <Dialog open={punchesOpen} onClose={() => setPunchesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            Today's Punches
          </Typography>
          <IconButton onClick={() => setPunchesOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {todayPunches.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mode of Entry</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todayPunches.map((punch: any, index: number) => (
                    <TableRow key={punch.id || index}>
                      <TableCell>{punch.date || new Date().toLocaleDateString('en-IN')}</TableCell>
                      <TableCell>{punch.time || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={punch.type}
                          size="small"
                          color={punch.type === 'CHECK IN' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{punch.modeOfEntry || 'Web Based Entry'}</TableCell>
                      <TableCell>{punch.remarks || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccessTime sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No punches recorded today.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPunchesOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WebPunchCard;
